import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AuthLayout from '../../components/layout/AuthLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Smartphone, Copy, Check } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../components/ui/input-otp';

const MFASetupPage = () => {
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const { setupMfa, verifyMfa, tempUserId } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!tempUserId) {
      navigate('/login');
      return;
    }

    const initSetup = async () => {
      try {
        const data = await setupMfa();
        setQrCode(data.qr_code);
        setSecret(data.secret);
      } catch (error) {
        toast.error('Failed to initialize MFA setup');
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    initSetup();
  }, [setupMfa, navigate, tempUserId]);

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    toast.success('Secret copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleVerify = async () => {
    if (token.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      await verifyMfa(token);
      toast.success('MFA enabled successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
      setToken('');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card className="border-0 shadow-xl">
        <CardHeader className="space-y-1 pb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Smartphone className="h-5 w-5 text-indigo-600" />
            </div>
          </div>
          <CardTitle className="font-outfit text-2xl font-semibold text-slate-900">
            Set up Two-Factor Authentication
          </CardTitle>
          <CardDescription className="text-slate-500">
            Scan this QR code with Google Authenticator or a compatible app
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* QR Code */}
          <div className="flex justify-center">
            <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
              {qrCode && (
                <img 
                  src={qrCode} 
                  alt="MFA QR Code" 
                  className="w-48 h-48"
                  data-testid="mfa-qr-code"
                />
              )}
            </div>
          </div>

          {/* Secret Key */}
          <div className="space-y-2">
            <p className="text-sm text-slate-600 text-center">
              Or enter this code manually:
            </p>
            <div className="flex items-center justify-center gap-2">
              <code className="px-4 py-2 bg-slate-100 rounded-lg font-mono text-sm text-slate-700">
                {secret}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopySecret}
                data-testid="copy-secret-btn"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Verification Code Input */}
          <div className="space-y-3">
            <p className="text-sm text-slate-600 text-center font-medium">
              Enter the 6-digit code from your authenticator app:
            </p>
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={token}
                onChange={(value) => setToken(value)}
                data-testid="mfa-setup-otp-input"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>

          <Button
            className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 font-medium"
            onClick={handleVerify}
            disabled={verifying || token.length !== 6}
            data-testid="mfa-setup-verify-btn"
          >
            {verifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Enable Two-Factor Authentication'
            )}
          </Button>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default MFASetupPage;
